import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { appointmentReminderJobService } from '../services/api';
import {
  CalendarIcon,
  SearchIcon,
  FilterIcon,
  RefreshIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  SendIcon,
  ArrowPathIcon
} from '../assets/icons';
import { useLanguage } from '../contexts/LanguageContext';
import Pagination from '../components/Pagination';
import SearchBar from '../components/SearchBar';

const AppointmentReminderJobs = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    isActive: true,
    jobStatus: '',
    version: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, jobId: null, jobVersion: null });
  const [resetConfirm, setResetConfirm] = useState({ show: false, jobId: null, jobVersion: null });

  // Status options for filter
  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'processing', label: 'Running' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  // Active status options for filter
  const activeOptions = [
    { value: null, label: 'All' },
    { value: true, label: 'Active' },
    { value: false, label: 'Inactive' }
  ];

  // Fetch jobs data
  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);

      const skip = (currentPage - 1) * itemsPerPage;
      const response = await appointmentReminderJobService.getAppointmentReminderJobs(
        skip,
        itemsPerPage,
        searchTerm || null,
        filters.isActive,
        filters.jobStatus || null,
        filters.version || null
      );

      setJobs(response.items || []);
      setTotal(response.total || 0);
    } catch (err) {
      console.error('Error fetching appointment reminder jobs:', err);
      setError('Failed to load appointment reminder jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when dependencies change
  useEffect(() => {
    fetchJobs();
  }, [currentPage, searchTerm, filters]);

  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      isActive: null,
      jobStatus: '',
      version: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Get status badge styling
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'running':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'running':
        return <ClockIcon className="w-4 h-4" />;
      case 'failed':
        return <XCircleIcon className="w-4 h-4" />;
      case 'cancelled':
        return <XCircleIcon className="w-4 h-4" />;
      case 'pending':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Handle row click to navigate to job entries
  const handleRowClick = (jobId) => {
    navigate(`/appointment-reminder-jobs/${jobId}/entries`);
  };

  // Handle delete job
  const handleDeleteJob = async (jobId) => {
    try {
      setLoading(true);
      await appointmentReminderJobService.deleteAppointmentReminderJob(jobId);
      setDeleteConfirm({ show: false, jobId: null, jobVersion: null });
      await fetchJobs(); // Refresh the list
    } catch (err) {
      console.error('Error deleting appointment reminder job:', err);
      setError('Failed to delete appointment reminder job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete button click
  const handleDeleteClick = (e, jobId, jobVersion) => {
    e.stopPropagation(); // Prevent row click
    setDeleteConfirm({ show: true, jobId, jobVersion });
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (deleteConfirm.jobId) {
      handleDeleteJob(deleteConfirm.jobId);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteConfirm({ show: false, jobId: null, jobVersion: null });
  };

  // Handle send job
  const handleSendJob = async (e, jobId) => {
    e.stopPropagation(); // Prevent row click
    try {
      setLoading(true);
      await appointmentReminderJobService.sendAppointmentReminderJob(jobId);
      await fetchJobs(); // Refresh the list
    } catch (err) {
      console.error('Error sending appointment reminder job:', err);
      setError('Failed to send appointment reminder job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle reset job
  const handleResetJob = async (e, jobId) => {
    if (e) {
      e.stopPropagation(); // Prevent row click
    }
    try {
      setLoading(true);
      await appointmentReminderJobService.resetAppointmentReminderJob(jobId);
      setResetConfirm({ show: false, jobId: null, jobVersion: null });
      await fetchJobs(); // Refresh the list
    } catch (err) {
      console.error('Error resetting appointment reminder job:', err);
      setError('Failed to reset appointment reminder job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle reset button click
  const handleResetClick = (e, jobId, jobVersion) => {
    e.stopPropagation(); // Prevent row click
    setResetConfirm({ show: true, jobId, jobVersion });
  };

  // Handle reset confirmation
  const handleResetConfirm = () => {
    if (resetConfirm.jobId) {
      handleResetJob(null, resetConfirm.jobId);
    }
  };

  // Handle reset cancel
  const handleResetCancel = () => {
    setResetConfirm({ show: false, jobId: null, jobVersion: null });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Appointment Reminder Jobs
                </h1>
                <p className="text-gray-600 mt-1">
                  Manage and monitor appointment reminder job executions
                </p>
              </div>
            </div>
            <button
              onClick={fetchJobs}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshIcon className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Search Bar */}
            <div className="flex-1">
              <SearchBar
                placeholder="Search by version or status..."
                onSearch={handleSearch}
                className="w-full"
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <FilterIcon className="w-4 h-4 mr-2" />
              Filters
            </button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Active Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Active Status
                  </label>
                  <select
                    value={filters.isActive === null ? '' : filters.isActive}
                    onChange={(e) => handleFilterChange('isActive', e.target.value === '' ? null : e.target.value === 'true')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {activeOptions.map(option => (
                      <option key={option.value} value={option.value === null ? '' : option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Status
                  </label>
                  <select
                    value={filters.jobStatus}
                    onChange={(e) => handleFilterChange('jobStatus', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Version Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Version
                  </label>
                  <input
                    type="text"
                    value={filters.version}
                    onChange={(e) => handleFilterChange('version', e.target.value)}
                    placeholder="Filter by version..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Clear Filters */}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <XCircleIcon className="w-5 h-5 text-red-400 mr-2" />
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Jobs Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Loading jobs...</span>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || Object.values(filters).some(f => f !== null && f !== '')
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No appointment reminder jobs have been created yet.'
                }
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 relative">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Version
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Active
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Send DateTime
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Updated
                      </th>
                      <th className="sticky right-0 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider z-10 border-l border-gray-200">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {jobs.map((job) => (
                      <tr
                        key={job.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleRowClick(job.id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                          {job.id.slice(-8)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {job.version || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(job.status)}`}>
                            {getStatusIcon(job.status)}
                            <span className="ml-1 capitalize">{job.status || 'Unknown'}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            job.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {job.is_active ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {job.send_datetime ? formatDate(job.send_datetime) : 'Manual'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(job.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(job.updated_at)}
                        </td>
                        <td className="sticky right-0 bg-white px-6 py-4 whitespace-nowrap text-sm text-gray-500 z-10 border-l border-gray-200">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => handleSendJob(e, job.id)}
                              disabled={loading || job.status === 'processing' || job.status === 'completed'}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded-md hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Send job"
                            >
                              <SendIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => handleResetClick(e, job.id, job.version)}
                              disabled={loading || job.status === 'pending'}
                              className="text-yellow-600 hover:text-yellow-900 p-1 rounded-md hover:bg-yellow-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Reset job to pending"
                            >
                              <ArrowPathIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => handleDeleteClick(e, job.id, job.version)}
                              className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50"
                              title="Delete job"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {total > itemsPerPage && (
                <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={Math.ceil(total / itemsPerPage)}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Summary */}
        {!loading && jobs.length > 0 && (
          <div className="mt-6 text-sm text-gray-500 text-center">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, total)} of {total} jobs
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {deleteConfirm.show && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <TrashIcon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mt-4">
                  Delete Appointment Reminder Job
                </h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete this appointment reminder job?
                    {deleteConfirm.jobVersion && (
                      <span className="block mt-1 font-medium">
                        Version: {deleteConfirm.jobVersion}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-red-600 mt-2">
                    This action cannot be undone and will delete all job entries.
                  </p>
                </div>
                <div className="items-center px-4 py-3">
                  <button
                    onClick={handleDeleteConfirm}
                    disabled={loading}
                    className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50"
                  >
                    {loading ? 'Deleting...' : 'Delete'}
                  </button>
                  <button
                    onClick={handleDeleteCancel}
                    disabled={loading}
                    className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md w-24 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reset Confirmation Dialog */}
        {resetConfirm.show && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                  <ArrowPathIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mt-4">
                  Reset Appointment Reminder Job
                </h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to reset this appointment reminder job to pending status?
                    {resetConfirm.jobVersion && (
                      <span className="block mt-1 font-medium">
                        Version: {resetConfirm.jobVersion}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-yellow-600 mt-2">
                    This will reset the job status and all entries to pending, clearing sent timestamps and error messages.
                  </p>
                </div>
                <div className="items-center px-4 py-3">
                  <button
                    onClick={handleResetConfirm}
                    disabled={loading}
                    className="px-4 py-2 bg-yellow-600 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-300 disabled:opacity-50"
                  >
                    {loading ? 'Resetting...' : 'Reset'}
                  </button>
                  <button
                    onClick={handleResetCancel}
                    disabled={loading}
                    className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md w-24 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentReminderJobs;
